import type { WaterData } from '../types';

const DHW_TYPE_LABELS: Record<string, string> = {
  'combi-on-demand': 'Combi on-demand',
  'vented-cylinder': 'Vented cylinder',
  'unvented-cylinder': 'Unvented cylinder',
  'thermal-store': 'Thermal store',
  unknown: 'Not assessed yet',
};

const VOLUME_LABELS: Record<string, string> = {
  small: 'Small (<90L)',
  'normal-vented': 'Normal vented (90–105L)',
  large: 'Large (120–180L)',
  xlarge: 'XL (200L+)',
  unknown: 'Not assessed yet',
};

const CIRC_LABELS: Record<string, string> = {
  none: 'None',
  'keep-hot': 'Keep-hot',
  'bronze-return-24-7': 'Bronze sec. return (24/7)',
  'bronze-return-timed': 'Bronze sec. return (timed)',
  unknown: 'Not assessed yet',
};

const CYLINDER_TYPE_LABELS: Record<string, string> = {
  'high-performance': 'High Performance',
  'standard-modern': 'Standard Modern',
  'legacy-vented': 'Legacy Vented',
  'vintage-uninsulated': 'Vintage Uninsulated',
  'direct-electric': 'Direct Electric',
  'mixergy-vented': 'Mixergy Vented (Smart)',
  'mixergy-unvented': 'Mixergy Unvented (Smart)',
  unknown: 'Unknown',
};

const HARDNESS_LABELS: Record<string, string> = {
  soft: 'Soft',
  moderate: 'Moderate',
  hard: 'Hard',
  'very-hard': 'Very hard',
  unknown: 'Not assessed yet',
};

const SCALE_LABELS: Record<string, string> = {
  low: 'Low',
  moderate: 'Moderate',
  heavy: 'Heavy',
  'silica-leaning': 'Silica-leaning',
  unknown: 'Not assessed yet',
};

interface Props {
  data: WaterData;
  onClick: () => void;
}

export function WaterTile({ data, onClick }: Props) {
  const v = (val: string, map: Record<string, string>) => map[val] ?? val ?? '—';
  const isUnknown = (val: string) => !val || val === 'unknown';

  const hasBronzeReturn = data.circulationMode === 'bronze-return-24-7' || data.circulationMode === 'bronze-return-timed';

  return (
    <div className="hub-tile" onClick={onClick} role="button" tabIndex={0}>
      <div className="hub-tile__header">
        <h3 className="hub-tile__title">Water (DHW)</h3>
        <span className={`hub-tile__badge hub-tile__badge--${data.confidence.level}`}>
          {data.confidence.level}
        </span>
      </div>
      <div className="hub-tile__body">
        <div className="hub-tile__row">
          <span className="hub-tile__label">DHW type</span>
          <span className={`hub-tile__value${isUnknown(data.dhwSystemType) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.dhwSystemType, DHW_TYPE_LABELS)}
          </span>
        </div>
        {data.dhwSystemType !== 'combi-on-demand' && (
          <>
            <div className="hub-tile__row">
              <span className="hub-tile__label">Cylinder</span>
              <span className={`hub-tile__value${isUnknown(data.cylinderVolumeBand) ? ' hub-tile__value--unknown' : ''}`}>
                {v(data.cylinderVolumeBand, VOLUME_LABELS)}
              </span>
            </div>
            <div className="hub-tile__row">
              <span className="hub-tile__label">Standing loss</span>
              <span className={`hub-tile__value${!data.standingLoss ? ' hub-tile__value--unknown' : ''}`}>
                {data.standingLoss || '—'}
                {data.cylinderTypeProfile && data.cylinderTypeProfile !== 'unknown' && ` · ${v(data.cylinderTypeProfile, CYLINDER_TYPE_LABELS)}`}
              </span>
            </div>
          </>
        )}
        <div className="hub-tile__row">
          <span className="hub-tile__label">Circulation</span>
          <span className={`hub-tile__value${isUnknown(data.circulationMode) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.circulationMode, CIRC_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Pipe run</span>
          <span className={`hub-tile__value${isUnknown(data.pipeRunBand) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.pipeRunBand, { short: 'Short', medium: 'Medium', long: 'Long', unknown: '—' })}
            {data.pipeInsulation && ` · insulated: ${data.pipeInsulation}`}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Water quality</span>
          <span className={`hub-tile__value${isUnknown(data.hardnessBand) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.hardnessBand, HARDNESS_LABELS)}
            {data.scaleProfile && data.scaleProfile !== 'unknown' && ` · ${v(data.scaleProfile, SCALE_LABELS)} scale`}
          </span>
        </div>
      </div>

      {hasBronzeReturn && (
        <div className="hub-tile__warnings">
          <span className="hub-tile__warning">Bronze return: comfort vs efficiency trade-off</span>
        </div>
      )}

      <span className="hub-tile__tap-hint">tap to edit</span>
    </div>
  );
}
